import { useEffect, useState, type FC } from "react";
import { useApolloClient } from "@apollo/client/react";
import type { InvoiceUploadUnitStatus } from "../../models/InvoiceUploadUnitStatus";
import { theme } from "../../theme";
import { HorizontalSpacer } from "../atoms/Spacers";
import TabBar from "../molecules/TabBar";
import Card from "../atoms/Card";
import CardBody from "../atoms/CardBody";
import styled from "styled-components";
import OutlinedButton from "../atoms/buttons/OutlinedButton";
import { RefreshIcon } from "../atoms/icons/RefreshIcon";
import ActionButton from "../atoms/buttons/ActionButton";
import { UploadIcon } from "../atoms/icons/UploadIcon";
import ClientUploadUnit from "../organisms/ClientUploadUnit";
import LoadingSpinner from "../atoms/LoadingSpinner";
import Alert from "../molecules/Alert";
import { SortingArrowsIcon } from "../atoms/icons/SortingArrowsIcon";
import type { InvoiceUploadUnitModel } from "../../models/InvoiceUploadUnitModel";
import { ClearIcon } from "../atoms/icons/ClearIcon";
import Modal from "../molecules/Modal";
import UploadDocumentModalContent from "../molecules/modals/UploadDocumentModalContent";
import { useTranslation } from "react-i18next";
import PaginationComponent from "../molecules/PaginationComponent";
import Lookup from "../organisms/Lookup";
import NoContentView from "../molecules/NoContentView";
import FilterChip from "../atoms/inputs/FilterChip";
import TextLinkButton from "../atoms/buttons/TextLinkButton";
import { Row } from "react-grid-system";
import BodySmall from "../atoms/typography/BodySmall";
import GET_ALL_UPLOAD_UNITS from "../../graphql/queries/documentHandler/getAllUploadUnits";
import type { SortDirection } from "../../models/SortDirection";
import TitleUF from "../atoms/typography/TitleUF";
import { useClientsWithUploadUnits } from "../../graphql/hooks/documentHandler/useClientsWithUploadUnits";

export const ACTIVE_STATUSES: Array<InvoiceUploadUnitStatus> = [
  "PROCESSED",
  "UNKNOWN",
  "UNPROCESSED",
  "PREPROCESSED",
  "PREPROCESSED_AWAITING_CLIENT_CONNECTION",
  "AUTO_PROCESSED",
];
export const DONE_STATUSES: Array<InvoiceUploadUnitStatus> = [
  "COMPLETED",
  "REJECTED",
];
const ITEMS_PER_PAGE = 10;

const FlexedDiv = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

const DocumentHandler: FC = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<string>("new");
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    return (
      (localStorage.getItem("documentHandlerSortDirection") as SortDirection) ||
      "ASC"
    );
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(
    undefined,
  );
  const [selectedDocuments, setSelectedDocuments] = useState<Array<string>>([]);
  const [uploadDocumentsModalOpen, setUploadDocumentsModalOpen] =
    useState(false);

  //client filter
  const [displayUnitsWithoutCompany, setDisplayUnitsWithoutCompany] =
    useState(false);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState<
    string | undefined
  >(undefined);
  const [
    selectedDocumentFromMultipleClientsOpen,
    setSelectedDocumentFromMultipleClientsOpen,
  ] = useState(false);

  useEffect(() => {
    localStorage.setItem("documentHandlerSortDirection", sortDirection);
  }, [sortDirection]);

  const { clientsWithUploadUnits, error, loading, refetch } =
    useClientsWithUploadUnits(
      displayUnitsWithoutCompany ? true : undefined,
      selectedTab === "new" ? ACTIVE_STATUSES : DONE_STATUSES,
      selectedCompanyFilter,
      sortDirection,
      currentPage * ITEMS_PER_PAGE,
      ITEMS_PER_PAGE,
    );

  const apolloClient = useApolloClient();

  const onClickChangeSorting = () => {
    if (sortDirection === "ASC") {
      setSortDirection("DESC");
    } else {
      setSortDirection("ASC");
    }
  };

  //Handle select functions
  const onToggleDocument = (documentId: string, clientIdentifier?: string) => {
    if (selectedDocuments.length === 0) {
      setSelectedDocuments([...selectedDocuments, documentId]);
      setSelectedClientId(clientIdentifier);
      return;
    }
    if (selectedClientId && selectedClientId !== clientIdentifier) {
      setSelectedDocumentFromMultipleClientsOpen(true);
      return;
    } else {
      //check if document is already checked
      if (selectedDocuments.includes(documentId)) {
        //remove document from list
        const filteredList = selectedDocuments.filter((x) => x !== documentId);
        setSelectedDocuments(filteredList);
      } else {
        //add document to list
        setSelectedClientId(clientIdentifier);
        setSelectedDocuments([...selectedDocuments, documentId]);
      }
    }
  };
  const checkIfDocumentIsSelected = (documentId: string) => {
    if (selectedDocuments.includes(documentId)) {
      return true;
    } else {
      return false;
    }
  };
  const checkIfAllDocumentsInUploadUnitIsSelected = (
    uploadUnit: InvoiceUploadUnitModel,
  ) => {
    if (!uploadUnit) {
      return false;
    } else {
      const allDocumentIds =
        uploadUnit.invoiceDocuments?.map((doc) => doc.id) || [];
      const allSelected = allDocumentIds.every((id) =>
        selectedDocuments.includes(id),
      );
      return allSelected;
    }
  };
  const onToggleUploadUnit = (
    uploadUnit: InvoiceUploadUnitModel,
    clientIdentifier?: string,
  ) => {
    if (!uploadUnit) {
      return;
    } else {
      const allDocumentIds =
        uploadUnit.invoiceDocuments?.map((doc) => doc.id) || [];
      const allSelected = allDocumentIds.every((id) =>
        selectedDocuments.includes(id),
      );
      if (allSelected) {
        //remove all documents from selected
        const filteredList = selectedDocuments.filter(
          (id) => !allDocumentIds.includes(id),
        );
        setSelectedDocuments(filteredList);
      } else {
        //add all documents to selected
        if (selectedClientId && selectedClientId !== clientIdentifier) {
          setSelectedDocumentFromMultipleClientsOpen(true);
          return;
        } else {
          setSelectedClientId(clientIdentifier);
          const newSelectedDocuments = [
            ...new Set([...selectedDocuments, ...allDocumentIds]),
          ];
          setSelectedDocuments(newSelectedDocuments);
        }
      }
    }
  };
  const onClearSelection = () => {
    setSelectedClientId(undefined);
    setSelectedDocuments([]);
    selectedDocumentFromMultipleClientsOpen &&
      setSelectedDocumentFromMultipleClientsOpen(false);
  };

  const onClickItem = (item: {
    clientIdentifier: string;
    clientType: string;
    companyName: string;
  }) => {
    if (item.clientIdentifier) {
      setSelectedCompanyFilter(item.clientIdentifier);
    }
  };

  const onClearClientFilter = () => {
    setSelectedCompanyFilter(undefined);
  };

  const onClickDisplayUnitsWithoutCompany = () => {
    if (displayUnitsWithoutCompany) {
      setDisplayUnitsWithoutCompany(!displayUnitsWithoutCompany);
    } else {
      setDisplayUnitsWithoutCompany(!displayUnitsWithoutCompany);
      setSelectedCompanyFilter(undefined);
    }
  };

  return (
    <div>
      <TitleUF color={theme.colors.primary}>
        {t("menu.documentHandler")}
      </TitleUF>
      <HorizontalSpacer spacing={1} />
      <TabBar
        tabs={[
          { id: "new", name: t("documentHandler.newDocuments") },
          { id: "registered", name: t("documentHandler.registeredDocuments") },
        ]}
        selectedTabId={selectedTab}
        onSelectTab={setSelectedTab}
      />

      <HorizontalSpacer spacing={2} />

      <Row nogutter justify="between" align="end">
        <FilterChip
          titleKey="documentHandler.onlyDisplayUnitsWithoutCompany"
          selected={displayUnitsWithoutCompany}
          onSelect={onClickDisplayUnitsWithoutCompany}
        />
        <FlexedDiv>
          <BodySmall color={theme.colors.textSecondary}>
            {clientsWithUploadUnits?.totalCount} {t("documentHandler.uploads")}
          </BodySmall>
          <ActionButton
            title={t("documentHandler.uploadDocument")}
            color={theme.colors.primary}
            icon={<UploadIcon color={theme.colors.white} />}
            onClick={() => setUploadDocumentsModalOpen(true)}
            small
          />
        </FlexedDiv>
      </Row>

      <HorizontalSpacer spacing={1} />

      <Row nogutter justify="between" align="end">
        <FlexedDiv>
          <OutlinedButton
            title={`${t("documentHandler.clearSelectedDocuments")} (${selectedDocuments.length}st)`}
            color={theme.colors.text}
            icon={<ClearIcon />}
            onClick={onClearSelection}
          />
          <Lookup
            type="dropdownSmall"
            onClickResult={onClickItem}
            onClear={onClearClientFilter}
            displayOnlyIssuer
            variant="light"
          />
        </FlexedDiv>

        <FlexedDiv>
          <TextLinkButton
            title={
              sortDirection === "ASC"
                ? t("documentHandler.showOldestFirst")
                : t("documentHandler.showNewestFirst")
            }
            icon={<SortingArrowsIcon color={theme.colors.primary} />}
            onClick={onClickChangeSorting}
            color={theme.colors.primary}
          />
          <OutlinedButton
            onClick={() => {
              refetch();
              apolloClient.refetchQueries({ include: [GET_ALL_UPLOAD_UNITS] });
            }}
            title={t("common.refresh")}
            color={theme.colors.primary}
            icon={<RefreshIcon color={theme.colors.primary} />}
            loading={loading}
          />
        </FlexedDiv>
      </Row>

      <HorizontalSpacer spacing={1.5} />

      <Card spacing={0} border style={{ minWidth: "75vw" }}>
        {error && (
          <CardBody>
            <Alert
              title={t("errors.somethingWentWrong")}
              type="error"
              message={error?.toString()}
            />
          </CardBody>
        )}
        {loading && (
          <CardBody>
            <LoadingSpinner />
          </CardBody>
        )}
        {!loading &&
          (!clientsWithUploadUnits ||
            clientsWithUploadUnits.totalCount === 0) && (
            <CardBody>
              <NoContentView />
            </CardBody>
          )}
        {clientsWithUploadUnits?.items.map((clientUploadUnit) => (
          <div key={clientUploadUnit?.client?.id ?? "noclient"}>
            <ClientUploadUnit
              totalCount={clientUploadUnit?.filteredTotalCount ?? 0}
              client={clientUploadUnit.client}
              selectedDocuments={selectedDocuments}
              checkIfAllDocumentsInUploadUnitIsSelected={
                checkIfAllDocumentsInUploadUnitIsSelected
              }
              checkIfDocumentIsSelected={checkIfDocumentIsSelected}
              onToggleDocument={onToggleDocument}
              onToggleUploadUnit={onToggleUploadUnit}
              selectedClientId={selectedClientId}
              onClearSelection={onClearSelection}
              statuses={selectedTab === "new" ? ACTIVE_STATUSES : DONE_STATUSES}
              sortDirection={sortDirection}
            />
          </div>
        ))}
        <PaginationComponent
          numberOfPages={Math.ceil(
            (clientsWithUploadUnits?.totalCount || 0) / ITEMS_PER_PAGE,
          )}
          currentPageIndex={currentPage}
          setCurrentPageIndex={setCurrentPage}
          spacing={1}
        />
      </Card>

      {uploadDocumentsModalOpen && (
        <Modal
          onClose={() => setUploadDocumentsModalOpen(false)}
          closeIcon
          width={800}
        >
          <UploadDocumentModalContent
            onClose={() => setUploadDocumentsModalOpen(false)}
          />
        </Modal>
      )}

      {selectedDocumentFromMultipleClientsOpen && (
        <Modal
          onClose={() => setSelectedDocumentFromMultipleClientsOpen(false)}
          closeIcon
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setSelectedDocumentFromMultipleClientsOpen(false),
            },
            outlinedButton: {
              buttonTitle: t("documentHandler.clearSelectedDocuments"),
              onClick: onClearSelection,
            },
          }}
        >
          <HorizontalSpacer spacing={1.5} />
          <BodySmall>
            {t("documentHandler.youMayOnlySelectDocumentsFromTheSameClient")}
          </BodySmall>
        </Modal>
      )}
    </div>
  );
};

export default DocumentHandler;
